import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { Mail, ArrowLeft, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import '../styles/App.css';

import BrandLogo from '../components/common/BrandLogo';
function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [touched, setTouched] = useState(false);

  const validateEmail = (email) => {
    return String(email).toLowerCase().match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
  };

  const isEmailValid = validateEmail(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);

    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await authAPI.resetPassword(email.trim().toLowerCase());
      setSuccess(res.data?.message || 'Kindly check your email to continue.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to process request. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '0.85rem 1rem 0.85rem 2.8rem',
    borderRadius: '12px', fontSize: '0.95rem',
    border: `1.5px solid ${touched && !isEmailValid ? '#ef4444' : 'var(--border-color)'}`,
    background: 'var(--bg-primary)', color: 'var(--text-primary)',
    transition: 'all 0.2s ease', outline: 'none',
    boxShadow: touched && !isEmailValid ? '0 0 0 4px rgba(239, 68, 68, 0.05)' : 'none'
  };

  return (
    <div className="auth-split-wrapper">
      {/* Branding Side */}
      <div className="auth-brand-side">
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: 'url("/auth-bg-login.png")',
          backgroundSize: 'cover', backgroundPosition: 'center',
          animation: 'slowZoom 30s infinite alternate ease-in-out',
          zIndex: 1
        }} />
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(to right, rgba(0,0,0,0.6), rgba(0,0,0,0.3))',
          zIndex: 2
        }} />
        
        <div style={{ position: 'relative', zIndex: 3, maxWidth: '600px', textAlign: 'left' }}>
          <div style={{ marginBottom: '2rem' }}>
            <BrandLogo style={{ fontSize: '2rem' }} />
          </div>
          <h1 style={{ 
            fontSize: '3.5rem', fontWeight: '900', color: 'white', 
            lineHeight: '1.1', marginBottom: '1.5rem', letterSpacing: '-0.02em'
          }}>
            Securing the Future of <span style={{ color: '#FCD535' }}>Quantitative Wealth.</span>
          </h1>
          <p style={{ 
            fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', 
            maxWidth: '500px', lineHeight: '1.6', fontWeight: '400' 
          }}>
            Recovery protocols are strictly monitored. Please follow the instructions sent to your registered institutional email.
          </p>
        </div>
      </div>

      {/* Form Side */}
      <div className="auth-form-side">
        <div className="auth-card glass" style={{ 
          maxWidth: '440px', width: '100%', padding: '3rem', 
          borderRadius: '32px', border: '1px solid var(--border-color)',
          background: 'var(--bg-modal)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
          animation: 'fadeInUp 0.4s ease-out'
        }}>
          <div style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ color: 'var(--text-primary)', fontSize: '2.2rem', fontWeight: '900', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>Recovery</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>Enter your email to initiate recovery protocols.</p>
          </div>

          {error && (
            <div style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444', padding: '1rem', borderRadius: '16px', marginBottom: '2rem', fontSize: '0.85rem', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
               <AlertCircle size={16} /> {error}
            </div>
          )}
          {success && (
            <div style={{ backgroundColor: 'rgba(16,185,129,0.1)', color: '#10b981', padding: '1rem', borderRadius: '16px', marginBottom: '2rem', fontSize: '0.85rem', border: '1px solid rgba(16,185,129,0.2)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
               <ShieldCheck size={16} /> {success}
            </div>
          )}

          {!success ? (
            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group" style={{ marginBottom: '2.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.85rem', fontWeight: '600', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Email Address</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '1.25rem', top: '50%', transform: 'translateY(-50%)', color: touched && !isEmailValid ? '#ef4444' : 'rgba(255,255,255,0.3)', transition: 'color 0.2s' }}>
                    <Mail size={18} />
                  </span>
                  <input 
                    type="email" 
                    value={email} 
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    onBlur={() => setTouched(true)}
                    placeholder="name@company.com" 
                    style={{
                      width: '100%',
                      padding: '1.1rem 1rem 1.1rem 3.2rem',
                      borderRadius: '16px',
                      border: `1.5px solid ${touched && !isEmailValid ? '#ef4444' : 'var(--border-color)'}`,
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => !(touched && !isEmailValid) && (e.target.style.borderColor = '#FCD535')}
                    onBlur={(e) => !(touched && !isEmailValid) && (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                  />
                </div>
              </div>

              <button type="submit" className="btn btn-primary" style={{ 
                width: '100%', padding: '1.25rem', borderRadius: '16px', fontSize: '1.1rem', 
                fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                gap: '0.75rem', background: 'linear-gradient(135deg, #FCD535 0%, #d97706 100%)',
                color: '#000', border: 'none', boxShadow: '0 15px 30px rgba(245, 158, 11, 0.25)',
                cursor: loading ? 'not-allowed' : 'pointer' 
              }} disabled={loading}>
                {loading ? <Loader2 size={22} className="spinner" /> : 'Request Reset Link'}
              </button>
            </form>
          ) : (
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
               <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', marginBottom: '2.5rem', lineHeight: '1.6' }}>We've dispatched a secure recovery link to <strong>{email}</strong>. Please check your institutional inbox and follow the authorization steps.</p>
               <Link to="/login" className="btn btn-primary" style={{ 
                 display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', 
                 textDecoration: 'none', padding: '1.1rem', borderRadius: '16px', 
                 fontSize: '1rem', fontWeight: '800',
                 background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)'
               }}>
                 Return to Login
               </Link>
            </div>
          )}

          <div className="form-footer" style={{ marginTop: '2.5rem', textAlign: 'center' }}>
            <Link to="/login" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontWeight: '600' }}>
              <ArrowLeft size={16} /> Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
